/**
 * QuickSwitchBar — Tab-bar for fast switching between activated visualizer modes.
 *
 * Matches the prototype `editor-drawer.html` viz-tabs pattern:
 * text labels in a horizontal bar with a violet underline on active.
 * Only shows user-selected modes (not all modes).
 * 'raw' is always the first tab and cannot be removed.
 */

import { Trash2 } from 'lucide-react';
import React, { useCallback, useEffect,useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { VisualizerModeRegistry } from '@/lib/VisualizerModeRegistry';
import type { VisualizerMode } from '@/store/useVisualizerStore';

export interface QuickSwitchBarProps {
  activeModes: VisualizerMode[];
  currentMode: string;
  onSwitchMode: (mode: VisualizerMode) => void;
  onRemoveMode: (mode: VisualizerMode) => void;
  /** All available modes for the "+" dropdown */
  availableModes?: VisualizerMode[];
  /** Called when a mode is added via the "+" button */
  onAddMode?: (mode: VisualizerMode) => void;
  /** Max user modes (excluding raw) */
  maxUserModes?: number;
}

export const QuickSwitchBar: React.FC<QuickSwitchBarProps> = ({
  activeModes,
  currentMode,
  onSwitchMode,
  onRemoveMode,
  availableModes = [],
  onAddMode,
  maxUserModes = 4,
}) => {
  // Modes not yet in the active list (candidates for "+")
  const addableModes = availableModes.filter(
    (m) => m !== 'raw' && !activeModes.includes(m),
  );
  const canAdd = activeModes.length - 1 < maxUserModes && addableModes.length > 0;

  return (
    <div className="flex items-center h-7 shrink-0 gap-0">
      {activeModes.map((mode) => (
        <VizTab
          key={mode}
          mode={mode}
          isActive={mode === currentMode}
          isRaw={mode === 'raw'}
          onSwitch={onSwitchMode}
          onRemove={onRemoveMode}
        />
      ))}
      {onAddMode && canAdd && (
        <AddModeButton addableModes={addableModes} onAdd={onAddMode} />
      )}
    </div>
  );
};

/* ─── Individual tab ──────────────────────────────────────────────── */

const VizTab: React.FC<{
  mode: VisualizerMode;
  isActive: boolean;
  isRaw: boolean;
  onSwitch: (mode: VisualizerMode) => void;
  onRemove: (mode: VisualizerMode) => void;
}> = ({ mode, isActive, isRaw, onSwitch, onRemove }) => {
  const def = VisualizerModeRegistry.get(mode);
  const label = def?.label ?? mode.charAt(0).toUpperCase() + mode.slice(1);

  const handleClick = useCallback(() => onSwitch(mode), [mode, onSwitch]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isRaw) return;
      e.preventDefault();
      onRemove(mode);
    },
    [mode, isRaw, onRemove],
  );

  return (
    <button
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className={cn(
        'group relative px-2 py-1 text-[11px] cursor-pointer transition-all duration-150 flex items-center gap-1',
        isActive
          ? 'text-violet-400'
          : 'text-muted-foreground/60 hover:text-muted-foreground',
      )}
    >
      {label}
      {/* Trash icon on hover for removable tabs */}
      {!isRaw && (
        <span
          onClick={(e) => { e.stopPropagation(); onRemove(mode); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-100 text-muted-foreground/40 hover:text-red-400"
          title="Remove"
        >
          <Trash2 className="h-2.5 w-2.5" />
        </span>
      )}
      {/* Active underline */}
      {isActive && (
        <span className="absolute bottom-0 left-1 right-1 h-[2px] bg-violet-500 rounded-sm" />
      )}
    </button>
  );
};

/* ─── "+" button with dropdown to add modes ──────────────────────── */

const AddModeButton: React.FC<{
  addableModes: VisualizerMode[];
  onAdd: (mode: VisualizerMode) => void;
}> = ({ addableModes, onAdd }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'px-1.5 py-1 text-[11px] cursor-pointer transition-all duration-150',
          'text-muted-foreground/40 hover:text-muted-foreground',
        )}
        title="Add visualizer mode"
      >
        +
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[120px] rounded-md border border-border bg-popover py-1 shadow-lg">
          {addableModes.map((mode) => {
            const def = VisualizerModeRegistry.get(mode);
            const label = def?.label ?? mode.charAt(0).toUpperCase() + mode.slice(1);
            const icon = def?.icon ?? mode.charAt(0).toUpperCase();
            return (
              <button
                key={mode}
                onClick={() => { onAdd(mode); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors flex items-center gap-2"
              >
                <span className="w-4 text-center text-[10px] text-muted-foreground/60">{icon}</span>
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


